import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FCW } from '../../types.ts';
import { isNil } from 'ramda';
import { AppConfig } from './AppConfigContext.ts';
import { getCsrfToken } from '@/httpClient/requests/getCsrfToken.ts';

export interface AppConfigApi {
  env: string;
  version: string;
  eventCode: string;
  api: {
    events: string;
    registrations: string;
    registrationsConfirmation: string;
    feedback: string;
    peselCheck: string;
    contactVerificationCheck: string;
    contactVerificationSend: string;
    searchAddress: string;
    lookupAddress: string;
  };
  isTestEvent: boolean;
  setIsTestEvent: Dispatch<SetStateAction<boolean>>;
}

interface MetaValues {
  env: string;
  version: string;
  event_code: string;
  api_events: string;
  api_registrations: string;
  api_registrations_confirmation: string;
  api_feedback: string;
  api_pesel_check: string;
  api_contact_verification_check: string;
  api_contact_verification_send: string;
  api_loc_search: string;
  api_loc_lookup: string;
}

const loadMetaTagValues = () => {
  // Load all Meta-tags with "dkms" in the name and put them into an object
  const meta = [...document.querySelectorAll('meta[name^=dkms]')];

  const conf = meta.reduce((acc, curr) => {
    const name = `${curr.getAttribute('name')}`.split(':')[2];
    const value = curr.getAttribute('content') || '';

    // parse Template variable names to something we can use in E2E Tests
    if (value.startsWith('{{')) {
      return { ...acc, [name]: `/local/${name}` };
    }

    return { ...acc, [name]: value };
  }, {} as Partial<MetaValues>);
  return conf;
};

export const AppConfigProvider: FCW = ({ children }) => {
  const [isTestEvent, setIsTestEvent] = useState<boolean>(false);
  const [metaConfig, setMetaConfig] = useState<MetaValues>(() => {
    return loadMetaTagValues() as MetaValues;
  });

  useEffect(() => {
    void getCsrfToken();
  }, []);

  const value = isNil(metaConfig)
    ? null
    : {
        env: metaConfig?.env,
        version: metaConfig?.version,
        eventCode: metaConfig?.event_code,
        api: {
          events: metaConfig?.api_events,
          registrations: metaConfig?.api_registrations,
          registrationsConfirmation: metaConfig?.api_registrations_confirmation,
          feedback: metaConfig?.api_feedback,
          peselCheck: metaConfig?.api_pesel_check,
          contactVerificationCheck: metaConfig?.api_contact_verification_check,
          contactVerificationSend: metaConfig?.api_contact_verification_send,
          searchAddress: metaConfig?.api_loc_search,
          lookupAddress: metaConfig?.api_loc_lookup,
        },
        isTestEvent: isTestEvent,
        setIsTestEvent: setIsTestEvent,
      };

  return <AppConfig.Provider value={value}>{children}</AppConfig.Provider>;
};
